import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { PublicHeader } from '../../components/shell/PublicHeader';
import { PublicFooter } from '../../components/shell/PublicFooter';
import { SEO } from '../../components/SEO';
import { cn } from '../../lib/utils';
import { TOOLS_LIST } from './ToolsDirectoryPage';
import { Badge, Button } from '../../components/ui';
import { useNavigate, Link } from 'react-router-dom';
import { Boxes, ArrowRight, HelpCircle } from 'lucide-react';

export type ToolHubId = 'hoa' | 'union' | 'youth-sports' | 'nonprofit' | 'church' | 'association' | 'greek-life' | 'alumni' | 'guild' | 'advocacy' | 'senior-living';

interface ToolHubPageProps {
  hub: ToolHubId;
}

const HUB_CONFIGS: Record<ToolHubId, { title: string; seoTitle: string; description: string; toolSlugs: string[] }> = {
  'hoa': {
    title: 'HOA Management Tools',
    seoTitle: 'Free HOA Management Tools & Calculators | OrgSets',
    description: 'Calculators and utilities designed specifically for Homeowner Associations, from reserve modeling to vendor bids and budget tracking.',
    toolSlugs: [
      'hoa-budget-tracker',
      'hoa-reserve-modeler',
      'hoa-bid-comparer',
      'meeting-minutes-generator',
      'conflict-of-interest-policy',
      'garden-bed-estimator',
      'volunteer-hours-log',
      'neighborhood-assessment-calculator',
      'community-land-trust-calculator',
      'hoa-special-assessment-calculator',
      'hoa-violation-door-hanger',
      'neighborhood-newsletter-template',
      'meeting-agenda-template',
      'roberts-rules-cheat-sheet',
      'wifi-qr-poster'
    ]
  },
  'union': {
    title: 'Labor Union Tools',
    seoTitle: 'Union Administration Tools & Calculators | OrgSets',
    description: 'Administrative tools for labor unions to manage dues splits, verify quorum, and handle parliamentary procedures.',
    toolSlugs: [
      'voting-weight-calculator',
      'quorum-and-roll-checker',
      'roberts-rules-motion-finder',
      'meeting-minutes-generator',
      'chapter-dues-splitter',
      'dues-split-calculator',
      'bylaws-templates-builder',
      'union-strike-fund-calculator',
      'union-wage-progression',
      'multi-chapter-ballot-validator',
      'roberts-rules-cheat-sheet',
      'meeting-agenda-template',
      'membership-certificate'
    ]
  },
  'youth-sports': {
    title: 'Youth Sports League Tools',
    seoTitle: 'Youth Sports League Tools & Calculators | OrgSets',
    description: 'Calculators for youth sports leagues to estimate fees, manage tournament budgets, and generate liability waivers.',
    toolSlugs: [
      'sports-fee-estimator',
      'sports-tournament-budget',
      'waiver-generator',
      'volunteer-hours-log',
      'volunteer-value-calculator',
      'scout-camp-planner',
      'fundraising-tier-calculator',
      'league-standings-tiebreaker',
      'swim-heat-sheet-generator',
      'travel-team-budget-planner',
      'round-robin-schedule-generator',
      'snake-draft-team-balancer',
      'tournament-bracket-generator',
      'emergency-roster-card',
      'sign-up-sheet-generator',
      'volunteer-sign-in-sheet',
      'raffle-ticket-generator'
    ]
  },
  'nonprofit': {
    title: 'Nonprofit Admin Tools',
    seoTitle: 'Nonprofit Admin Tools & Free Calculators | OrgSets',
    description: 'Essential calculators and generators for 501(c)(3) nonprofits, covering fundraising, volunteer tracking, and compliance.',
    toolSlugs: [
      'fundraising-tier-calculator',
      'volunteer-value-calculator',
      'volunteer-hours-log',
      'bylaws-templates-builder',
      'meeting-minutes-generator',
      'conflict-of-interest-policy',
      'member-acquisition-cost',
      'gala-catering-calculator',
      'booster-grant-scanner',
      'pta-budget-vs-actuals',
      'board-skill-gap-analyzer',
      'nonprofit-990-ratio-checker',
      'endowment-runway-calculator',
      'philanthropy-profit-margin-predictor',
      'donation-receipt-template',
      'silent-auction-bid-sheet',
      'raffle-ticket-generator',
      'sign-up-sheet-generator',
      'volunteer-sign-in-sheet',
      'meeting-agenda-template',
      'banquet-place-cards'
    ]
  },
  'church': {
    title: 'Church Administration Tools',
    seoTitle: 'Church Admin & Budget Tools | OrgSets',
    description: 'Free tools for church administrators to estimate budgets, track volunteer hours, and plan community events.',
    toolSlugs: [
      'church-budget-estimator',
      'volunteer-hours-log',
      'fundraising-tier-calculator',
      'meeting-minutes-generator',
      'bylaws-templates-builder',
      'scout-camp-planner',
      'church-capital-campaign',
      'church-small-group-rebalancer',
      'pta-budget-vs-actuals',
      'worship-service-run-sheet',
      'donation-receipt-template',
      'sign-up-sheet-generator',
      'volunteer-sign-in-sheet',
      'meeting-agenda-template',
      'banquet-place-cards',
      'book-club-pamphlet',
      'silent-auction-bid-sheet'
    ]
  },
  'association': {
    title: 'Association Management Tools',
    seoTitle: 'Association Management Tools & Trackers | OrgSets',
    description: 'Utilities for professional and trade associations to split dues, track CEUs, and evaluate member retention.',
    toolSlugs: [
      'chapter-dues-splitter',
      'ceu-tracker',
      'member-retention-estimator',
      'member-retention-calculator',
      'sponsorship-package-pricer',
      'sponsorship-roi-calculator',
      'bylaws-templates-builder',
      'meeting-minutes-generator',
      'conference-pnl-simulator',
      'certification-pricing-calculator',
      'multi-chapter-ballot-validator',
      'ceu-certificate-maker',
      'membership-certificate',
      'meeting-agenda-template',
      'event-itinerary-builder',
      'banquet-place-cards',
      'roberts-rules-cheat-sheet'
    ]
  },
  'greek-life': {
    title: 'Greek Life Tools',
    seoTitle: 'Fraternity & Sorority Tools & Calculators | OrgSets',
    description: 'Calculators and utilities for fraternities, sororities, and Panhellenic councils to manage rush ROI, composites, and house rent.',
    toolSlugs: [
      'greek-rush-roi',
      'composite-photo-calculator',
      'chapter-house-rent-modeler',
      'panhellenic-quota-calculator',
      'multi-chapter-ballot-validator',
      'philanthropy-profit-margin-predictor',
      'snake-draft-team-balancer',
      'tournament-bracket-generator',
      'roberts-rules-cheat-sheet',
      'event-itinerary-builder'
    ]
  },
  'alumni': {
    title: 'Alumni Association Tools',
    seoTitle: 'Alumni Association Tools & Calculators | OrgSets',
    description: 'Specialized tools for university and school alumni chapters to map density, plan reunions, and model fundraising goals.',
    toolSlugs: [
      'alumni-density-mapper',
      'alumni-annual-fund-modeler',
      'alumni-mentorship-capacity',
      'class-reunion-estimator',
      'booster-grant-scanner',
      'endowment-runway-calculator',
      'reunion-name-tags',
      'tournament-bracket-generator'
    ]
  },
  'guild': {
    title: 'Professional Guild Tools',
    seoTitle: 'Professional Guild & Union Tools | OrgSets',
    description: 'Essential utilities for trade unions, freelancer collectives, and craft guilds to track ratios and build rate cards.',
    toolSlugs: [
      'guild-ratio-checker',
      'guild-rate-card-builder',
      'union-strike-fund-calculator',
      'union-wage-progression',
      'certification-pricing-calculator',
      'storyboard-grid-printable',
      'ceu-certificate-maker',
      'membership-certificate',
      'storefront-notice-sign'
    ]
  },
  'advocacy': {
    title: 'Community Advocacy Tools',
    seoTitle: 'Community Advocacy & Civic Action Tools | OrgSets',
    description: 'Analytical tools for civic groups and political campaigns to optimize canvassing, forecast signatures, and verify PAC compliance.',
    toolSlugs: [
      'canvassing-route-optimizer',
      'ballot-initiative-forecaster',
      'pac-contribution-checker',
      'neighborhood-assessment-calculator',
      'community-land-trust-calculator',
      'political-door-hanger',
      'volunteer-sign-in-sheet'
    ]
  },
  'senior-living': {
    title: 'Senior Living Tools',
    seoTitle: 'Senior Living Activity Tools & Generators | OrgSets',
    description: 'Engaging utilities and randomizers for senior living communities, including printable games and event management tools.',
    toolSlugs: [
      'bingo-caller-board',
      'bingo-card-generator',
      'digital-raffle-drum',
      'virtual-prize-wheel',
      'interactive-scoreboard',
      'volunteer-value-calculator',
      'event-budget-estimator',
      'ice-breaker-slot-machine',
      'raffle-ticket-generator',
      'book-club-pamphlet'
    ]
  }
};

export function ToolHubPage({ hub }: ToolHubPageProps) {
  const navigate = useNavigate();

  const config = HUB_CONFIGS[hub];

  const hubTools = useMemo(() => {
    return config.toolSlugs
      .map(slug => TOOLS_LIST.find(t => t.slug === slug))
      .filter(Boolean) as typeof TOOLS_LIST;
  }, [config.toolSlugs]);

  const toolSchema = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": hubTools.map((tool, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://orgsets.com/tools/${tool.slug}`,
        "name": tool.title,
        "description": tool.description
      }))
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://orgsets.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Tools",
          "item": "https://orgsets.com/tools"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": config.title,
          "item": `https://orgsets.com/tools/${hub}`
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300 bg-white text-navy-900 dark:text-white dark:bg-[#050505]">
      <SEO 
        title={config.seoTitle} 
        description={config.description}
        url={`https://orgsets.com/tools/${hub}`}
        schema={toolSchema}
      />
      <PublicHeader />

      <main className="pt-28 pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <header className="mb-16 text-center space-y-4">
            <Badge variant="gray" className="mb-2">OrgSets Tools for {config.title.split(' ')[0]}</Badge>
            <h1 className="text-4xl md:text-7xl font-black font-display tracking-tight leading-none bg-gradient-to-r from-navy-900 via-blue-800 to-navy-900 dark:from-white dark:via-blue-300 dark:to-white bg-clip-text text-transparent px-4 py-2 break-words">
              {config.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto">
              {config.description}
            </p>
          </header>

          {/* Hub Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-16 relative z-20">
            <Link to="/tools">
              <Badge variant="gray" className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-200 transition-colors">All Tools</Badge>
            </Link>
            <Link to="/tools/hoa">
              <Badge variant={hub === 'hoa' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">HOA Tools</Badge>
            </Link>
            <Link to="/tools/union">
              <Badge variant={hub === 'union' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Union Tools</Badge>
            </Link>
            <Link to="/tools/youth-sports">
              <Badge variant={hub === 'youth-sports' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Youth Sports</Badge>
            </Link>
            <Link to="/tools/nonprofit">
              <Badge variant={hub === 'nonprofit' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Nonprofit Tools</Badge>
            </Link>
            <Link to="/tools/church">
              <Badge variant={hub === 'church' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Church Tools</Badge>
            </Link>
            <Link to="/tools/association">
              <Badge variant={hub === 'association' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Association Tools</Badge>
            </Link>
            <Link to="/tools/greek-life">
              <Badge variant={hub === 'greek-life' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Greek Life</Badge>
            </Link>
            <Link to="/tools/alumni">
              <Badge variant={hub === 'alumni' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Alumni</Badge>
            </Link>
            <Link to="/tools/guild">
              <Badge variant={hub === 'guild' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Professional Guilds</Badge>
            </Link>
            <Link to="/tools/advocacy">
              <Badge variant={hub === 'advocacy' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Advocacy Tools</Badge>
            </Link>
            <Link to="/tools/senior-living">
              <Badge variant={hub === 'senior-living' ? 'gray' : 'blue'} className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">Senior Living</Badge>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {hubTools.map((tool, idx) => (
              <motion.div
                key={tool.slug}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/50 p-8 rounded-[2rem] border-2 border-black dark:border-white dark:bg-slate-900 dark:border-white/60 hover:border-blue-500/50 transition-all flex flex-col justify-between group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative"
              >
                <Link
                  to={`/tools/${tool.slug}`}
                  className="absolute inset-0 z-10 rounded-[2rem]"
                  aria-label={`Open tool: ${tool.title}`}
                />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-none bg-white dark:bg-slate-950 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex items-center justify-center relative z-20">
                      {tool.icon}
                    </div>
                    <div className="flex gap-2 relative z-20 pointer-events-none">
                      <Badge variant="gray" className="text-[10px] uppercase">{tool.category}</Badge>
                      {tool.badge && (
                        <Badge variant="blue" className="text-[10px] uppercase font-bold">{tool.badge}</Badge>
                      )}
                    </div>
                  </div>
                  <h3 className="text-2xl font-black font-display text-navy-900 dark:text-white group-hover:text-blue-600 transition-colors">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed dark:text-gray-400">
                    {tool.description}
                  </p>
                </div>
                <div className="pt-6 mt-6 border-t border-black dark:border-white dark:border-white/40 flex justify-between items-center text-xs font-bold text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors relative z-20 pointer-events-none">
                  <span className="flex items-center gap-1">
                    <Boxes className="w-3.5 h-3.5" /> Open Calculator
                  </span>
                  <div className="w-8 h-8 rounded-none border-2 border-black dark:border-white dark:border-white flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white group-hover:border-none transition-all">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center relative z-20">
             <Link to="/tools" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-2 dark:text-blue-400 dark:hover:text-blue-300">
                View All Tools <ArrowRight className="w-4 h-4" />
             </Link>
          </div>

          <div className="mt-20 text-center max-w-md mx-auto space-y-4 relative z-20">
            <HelpCircle className="w-8 h-8 text-gray-300 mx-auto" />
            <h4 className="font-bold font-display text-lg">Ready to keep this work in one place?</h4>
            <p className="text-xs text-gray-500 leading-relaxed">
              OrgSets gives {config.title.toLowerCase().replace(' tools', '')} one shared home for rosters, dues, documents, and announcements.
              Free for up to 50 members.
            </p>
            <Button onClick={() => navigate('/sign-up?source=tool&redirect_url=/dashboard')} variant="primary" size="sm" className="cursor-pointer relative z-20 font-bold uppercase tracking-wider">
              Create Free Organization
            </Button>
            <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-400">No credit card required</p>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
